import { Country } from './country.model';
import { ApiDate } from './api-response.model';
import type { Project } from './project.model';
import { ServiceType } from '../constants/api.constants';
export type { ServiceType };

// ===========================
// ENUMS / TYPE ALIASES
// ===========================

/**
 * Request type for opportunities
 */
export type RequestType = 'new' | 'enhancement';

/**
 * Referral source options
 */
export type ReferralSource =
  | 'google'
  | 'facebook'
  | 'instagram'
  | 'linkedin'
  | 'friend'
  | 'company'
  | 'online_advertisement'
  | 'event'
  | 'sales_representative'
  | 'partner'
  | 'other';

/**
 * Opportunity status values
 * - created: Initial status on creation
 * - ready: Ready for estimation
 * - estimated: Has been estimated
 * - sent: Proposal sent to client
 * - negotiation: In negotiation phase
 * - accepted: Client accepted
 * - rejected: Client rejected
 */
export type OpportunityStatus =
  | 'created'
  | 'ready'
  | 'estimated'
  | 'sent'
  | 'negotiation'
  | 'accepted'
  | 'rejected';

/**
 * Opportunity status enum constants
 * Use these constants instead of hardcoded strings
 */
export const OpportunityStatusValue = {
  NEW: 'created' as const,
  READY: 'ready' as const,
  ESTIMATED: 'estimated' as const,
  SENT: 'sent' as const,
  NEGOTIATION: 'negotiation' as const,
  ACCEPTED: 'accepted' as const,
  REJECTED: 'rejected' as const,
} as const;

/**
 * Badge variant type for status display
 * Maps to UI component badge colors
 */
export type StatusBadgeVariant =
  | 'primary'
  | 'secondary'
  | 'success'
  | 'warning'
  | 'error'
  | 'deep';

/**
 * Centralized mapping of opportunity status to badge variant (color)
 * - created: Black/Deep (color/surface-bg/neutral/deep)
 * - ready: Orange/Warning (color/surface-bg/warning/primary)
 * - estimated: Green/Success (color/surface-bg/success/primary)
 * - sent: Blue/Primary (color/surface-bg/primary/primary)
 * - negotiation: Orange/Warning (color/surface-bg/warning/primary)
 * - accepted: Green/Success (color/surface-bg/success/primary)
 * - rejected: Red/Error (color/surface-bg/destructive/primary)
 */
export const OPPORTUNITY_STATUS_BADGE_MAP: Record<
  OpportunityStatus,
  StatusBadgeVariant
> = {
  created: 'deep',
  ready: 'warning',
  estimated: 'success',
  sent: 'primary',
  negotiation: 'warning',
  accepted: 'success',
  rejected: 'error',
} as const;

/**
 * Editable status values (allowed in update request)
 * Only these values can be set via API when current status is "estimated"
 */
export type EditableOpportunityStatus =
  | 'sent'
  | 'negotiation'
  | 'accepted'
  | 'rejected';

// ===========================
// RESOURCE OBJECTS (API Response)
// ===========================

/**
 * Status object returned from API
 */
export interface StatusObject {
  value: OpportunityStatus;
  label: string;
  color: {
    name: 'gray' | 'blue' | 'yellow' | 'green' | 'red' | 'purple';
  };
}

/**
 * Client resource in opportunity response
 */
export interface ClientResource {
  id: number;
  name: string;
}

/**
 * Contract type resource in opportunity response
 */
export interface ContractTypeResource {
  value: string;
  label: string;
}

/**
 * Media/attachment resource
 */
export interface MediaResource {
  uuid: string;
  mime_type: string;
  name: string;
  url: string;
  size: number;
}

// ===========================
// MAIN ENTITY INTERFACE
// ===========================

/**
 * Opportunity entity from API response
 * Required fields are for lightweight/list views
 * Optional fields are for full detail views
 */
export interface Opportunity {
  // Required fields (used in list/lightweight views)
  id: number;
  name: string;
  client: ClientResource;
  contract_type: ContractTypeResource;
  request_type: RequestType;
  status: StatusObject;
  status_editable?: boolean;
  available_statuses?: StatusOption[];
  services: (ServiceType | { label: string; value: ServiceType })[];
  created_at: ApiDate;

  // Optional fields (used in full/detail views)
  referral_source?: ReferralSource;
  regional_market?: Country | null;
  service_deliverables?: (string | { value: string; label: string })[];
  attachments?: MediaResource[];
  description?: string | null;
  business_problem?: string | null;
  desired_outcomes?: string | null;
  updated_at?: ApiDate;
}

// ===========================
// APPROVAL RESPONSE INTERFACE
// ===========================

/**
 * Enriched response returned when opportunity status changes to 'accepted'
 * Includes the updated opportunity, newly created project, and potentially converted account
 * Used by the approval flow to show success feedback and navigate to the project
 */
export interface ApproveOpportunityResponse {
  message: string;
  data: {
    opportunity: Opportunity; // Updated opportunity with project_id and editable:false
    project: Project; // Newly created project with inherited fields
    account: {
      // Updated account (potentially Lead → Client conversion)
      id: number;
      name: string;
      type: 'lead' | 'client';
    };
  };
}

// ===========================
// REQUEST INTERFACE
// ===========================

/**
 * Request body for creating or updating an opportunity
 * All fields are required for creation (except optional fields)
 * All fields are optional for updates
 * Status can ONLY be updated when current status is "estimated"
 */
export interface OpportunityRequest {
  name?: string;
  client_id?: number;
  contract_type?: string;
  request_type?: RequestType;
  services?: ServiceType[];
  referral_source?: ReferralSource;
  regional_market?: string; // ISO Alpha-2 code (e.g., "EG", "SA")
  attachments?: { uuid: string }[]; // Array of objects with UUID from media upload
  description?: string;
  business_problem?: string;
  desired_outcomes?: string;
  status?: EditableOpportunityStatus;
}

// ===========================
// DROPDOWN OPTION INTERFACES
// ===========================

/**
 * Account option for dropdown selection
 * Includes displayName for showing "Name - Type" format
 */
export interface AccountOption {
  id: number;
  name: string;
  type: 'lead' | 'client';
  displayName?: string; // Computed: "Name - Lead" or "Name - Client"
}

/**
 * Status option for dropdown
 */
export interface StatusOption {
  value: OpportunityStatus;
  label: string;
}

/**
 * Request type option for dropdown
 */
export interface RequestTypeOption {
  value: RequestType;
  label: string;
}

/**
 * Referral source option for dropdown
 */
export interface ReferralSourceOption {
  value: ReferralSource;
  label: string;
}

/**
 * Service option for checkbox dropdown
 */
export interface ServiceOption {
  id: number;
  name: string;
}

// ===========================
// LIST RESPONSE INTERFACE
// ===========================

/**
 * Lightweight opportunity for list views
 * Contains only essential fields for table display
 */
export interface OpportunityLightweight {
  id: number;
  name: string;
  client: ClientResource;
  contract_type: ContractTypeResource;
  request_type: RequestType;
  status: StatusObject;
  services: (ServiceType | { label: string; value: ServiceType })[];
  regional_market?: Country | null;
  created_at: ApiDate;
}

/**
 * List opportunities response with pagination
 */
export interface OpportunityListResponse {
  message: string;
  data: OpportunityLightweight[];
  links: {
    first: string | null;
    last: string | null;
    prev: string | null;
    next: string | null;
  };
  meta: {
    current_page: number;
    from: number | null;
    last_page: number;
    per_page: number;
    to: number | null;
    total: number;
    path?: string;
  };
  sorting: string[]; // List of sortable column keys
}
